/**
 * Parallaxe au defilement, sans dependance.
 *
 * Le demo de reference (Lenis + GSAP ScrollTrigger) apporte ~90 ko de
 * JavaScript pour deplacer des images de quelques dizaines de pixels. Ici un
 * seul ecouteur passif, une boucle rAF, et une variable CSS : le navigateur
 * fait le reste sur le compositeur.
 *
 * L'element recoit --par, de -1 quand il entre par le bas de la fenetre a +1
 * quand il sort par le haut, 0 au centre. Les enfants s'en servent comme ils
 * veulent : translate3d(0, calc(var(--par) * 30px), 0).
 *
 * Rien ne bouge sous prefers-reduced-motion, et rien ne s'execute au rendu
 * serveur : --par vaut 0 par defaut, la page pre-rendue est deja bien posee.
 */

import { useEffect, type RefObject } from "react";

export function useParallaxe(ref: RefObject<HTMLElement>) {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    let brut = 0;
    let enAttente = false;

    const mesurer = () => {
      enAttente = false;
      const r = el.getBoundingClientRect();
      const h = window.innerHeight;
      // Centre de l'element rapporte au centre de la fenetre.
      const p = (r.top + r.height / 2 - h / 2) / (h / 2 + r.height / 2);
      const borne = Math.max(-1, Math.min(1, p));
      // On n'ecrit que si ca a bouge d'assez pour se voir.
      if (Math.abs(borne - brut) < 0.002) return;
      brut = borne;
      el.style.setProperty("--par", borne.toFixed(4));
    };

    const planifier = () => {
      if (enAttente) return;
      enAttente = true;
      requestAnimationFrame(mesurer);
    };

    mesurer();
    window.addEventListener("scroll", planifier, { passive: true });
    window.addEventListener("resize", planifier);
    return () => {
      window.removeEventListener("scroll", planifier);
      window.removeEventListener("resize", planifier);
    };
  }, [ref]);
}

/**
 * Meme idee, mais pour un element FIXE dans la fenetre.
 *
 * useParallaxe lit la position de l'element ; une couche fixe ne bouge jamais,
 * donc son --par resterait bloque a zero. Ici la mesure vient du defilement du
 * document : -1 en haut de page, +1 en bas.
 */
export function useDefilementPage(ref: RefObject<HTMLElement>) {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    let brut = 0;
    let enAttente = false;

    const mesurer = () => {
      enAttente = false;
      const piste = document.documentElement.scrollHeight - window.innerHeight;
      const p = piste > 0 ? (window.scrollY / piste) * 2 - 1 : 0;
      const borne = Math.max(-1, Math.min(1, p));
      if (Math.abs(borne - brut) < 0.002) return;
      brut = borne;
      el.style.setProperty("--par", borne.toFixed(4));
    };

    const planifier = () => {
      if (enAttente) return;
      enAttente = true;
      requestAnimationFrame(mesurer);
    };

    mesurer();
    window.addEventListener("scroll", planifier, { passive: true });
    window.addEventListener("resize", planifier);
    return () => {
      window.removeEventListener("scroll", planifier);
      window.removeEventListener("resize", planifier);
    };
  }, [ref]);
}

/**
 * Entree accrochee au defilement : --ent passe de 0 a 1 pendant que l'element
 * monte du bas de la fenetre jusqu'a sa place.
 *
 * Ce n'est pas une animation declenchee, c'est un scrub : la position suit la
 * molette image par image, et repart en arriere si on remonte.
 *
 * La valeur est bornee a 1 : une fois arrivee, la main reste. Sans ce plafond,
 * continuer a descendre la ferait repartir pendant qu'on lit le formulaire.
 *
 * Par defaut --ent vaut 1 cote CSS. Sans script, ou avant l'hydratation,
 * l'element est donc a sa place et jamais hors champ.
 */
export function useEntreeScrollee(ref: RefObject<HTMLElement>) {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    let brut = -1;
    let enAttente = false;

    const mesurer = () => {
      enAttente = false;
      const r = el.getBoundingClientRect();
      const h = window.innerHeight;
      // Le trajet s'acheve quand le haut de l'element a remonte de 55 % de la
      // hauteur de fenetre : la main est alors bien dans le champ.
      const p = (h - r.top) / (h * 0.55);
      const borne = p < 0 ? 0 : p > 1 ? 1 : p;
      if (Math.abs(borne - brut) < 0.002) return;
      brut = borne;
      el.style.setProperty("--ent", borne.toFixed(4));
    };

    const planifier = () => {
      if (enAttente) return;
      enAttente = true;
      requestAnimationFrame(mesurer);
    };

    mesurer();
    window.addEventListener("scroll", planifier, { passive: true });
    window.addEventListener("resize", planifier);
    return () => {
      window.removeEventListener("scroll", planifier);
      window.removeEventListener("resize", planifier);
    };
  }, [ref]);
}
